// @ts-check
// src/utils/session.js
// 2026-05-26 EXTRACTED from src/App.jsx (L1387-1406) as part of atomic apiAction
// Authorization header fix.
//
// Why extract:
//   - src/utils/api-actions.js needs getSessionToken к include Authorization
//     header в `buildHeaders`. Importing it from App.jsx would create unwanted
//     coupling к the 6737-LOC monolith (build-time graph, test-time mocking,
//     circular-dependency risk).
//   - This file owns the in-memory `sessionToken` cache + localStorage bridge.
//     All three accessors (`getSessionToken`, `setSessionToken`,
//     `resetSessionTokenCache`) share the SAME module-level binding — splitting
//     them would re-introduce the "two parallel sessionToken caches" bug.
//   - App.jsx re-exports `getSessionToken` + `resetSessionTokenCache` (used by
//     src/App.test.jsx and src/pages/Settings/index.jsx — backwards-compat
//     preserved). `setSessionToken` stays non-exported from App.jsx (was never
//     exported before; only consumed in-file).
//
// Storage keys (legacy — DO NOT rename без migration):
//   - `blitz_token` — written by login flow (App.jsx setSessionToken). Primary.
//   - `blitz_session` — JSON blob written by saveSession. Fallback used if
//     `blitz_token` absent (e.g. user landed via session-restore path).
//
// Threading model: single-threaded JS — no race on `sessionToken` mutation.

// Module-level cache. NOT exported — accessors are the only contract.
// Initialised lazily by getSessionToken (cold-start: hydrate from localStorage).
/** @type {string | null} */
let sessionToken = null;

/**
 * Persist а new session token (or clear it).
 *
 * @param {string | null} token New token, or `null` к clear and remove from localStorage.
 * @returns {void}
 */
export function setSessionToken(token) {
  sessionToken = token;
  if (token) {
    localStorage.setItem('blitz_token', token);
  } else {
    localStorage.removeItem('blitz_token');
  }
}

/**
 * Reset the in-memory cache (force re-read from localStorage on next call).
 * Used by code paths that suspect the cache is stale (server restart, fresh
 * token, race с background tab). Does NOT touch localStorage — pure cache-bust.
 *
 * @returns {void}
 */
export function resetSessionTokenCache() {
  sessionToken = null;
}

/**
 * Read the raw `blitz_session` blob from localStorage, no TTL check applied (see
 * App.jsx `getSession()` for the TTL-aware boot-path reader, which now delegates its
 * JSON-parse step to this function).
 *
 * 2026-08-31 (Phase 1 session TTL, П1/П5): extracted here — not App.jsx — because both
 * App.jsx (saveSession's loginTime-preserve logic) and the create-submit gates on
 * Dashboard/CustomerPayments need the same raw read, and `getSession`/`saveSession`
 * stay module-private in App.jsx (see file header: importing from App.jsx there would
 * create the exact monolith coupling this module exists to avoid).
 *
 * @returns {{loginTime?: number, token?: string, [key: string]: any} | null}
 */
export function getRawSession() {
  try {
    const s = localStorage.getItem('blitz_session');
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

/**
 * Read the current session token. Strategy:
 *   1. Return in-memory cache when populated.
 *   2. Hydrate from `localStorage.blitz_token` (set by setSessionToken).
 *   3. Fallback: parse `localStorage.blitz_session` JSON and extract `.token`.
 *   4. Return `null` (anonymous request — caller decides 401 UX).
 *
 * @returns {string | null} Token string or `null` when absent.
 */
export function getSessionToken() {
  if (sessionToken) return sessionToken;
  // Try blitz_token first (set by server auth)
  const t = localStorage.getItem('blitz_token');
  if (t) {
    sessionToken = t;
    return sessionToken;
  }
  // Fallback: check blitz_session (set by saveSession)
  try {
    const s = JSON.parse(localStorage.getItem('blitz_session') || '{}');
    if (s.token) {
      sessionToken = s.token;
      return sessionToken;
    }
  } catch {
    // Malformed JSON в localStorage — swallow, treat as absent.
  }
  return null;
}
